<template>
    <v-card class="elevation-3 mx-auto my-4 rounded border">
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="isFormValid" lazy-validation>
          <!-- Section 1: File & Identifiers -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-card-account-details-outline</v-icon>
            {{ isPrivatePractice ? 'Patient File Identifiers' : 'Client Case Identifiers' }}
          </v-subheader>
  
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.reference_number"
                label="File Reference Number"
                outlined
                readonly
                dense
                @change="updateField('reference_number')"
                prepend-inner-icon="mdi-pound"
              />
            </v-col>
  
            <v-col cols="12" sm="4">
              <v-text-field
                readonly
                v-model="form.client_reference"
                :label="isPrivatePractice ? 'Patient Ref / ID' : 'Client Ref / ID'"
                outlined
                dense
                @change="updateField('client_reference')"
                prepend-inner-icon="mdi-account"
              />
            </v-col>
  
            <v-col cols="12" sm="4">
              <v-text-field
                readonly
                v-model="form.practitioner_reference"
                :label="isPrivatePractice ? 'Practitioner Ref' : 'Caseworker Ref'"
                outlined
                dense
                @change="updateField('practitioner_reference')"
                :prepend-inner-icon="isPrivatePractice ? 'mdi-doctor' : 'mdi-account-tie'"
              />
            </v-col>
  
            <!-- <v-col cols="12" sm="6">
              <v-text-field
                readonly
                v-model="form.invoice_number"
                label="Invoice Number"
                outlined
                dense
                @change="updateField('invoice_number')"
                prepend-inner-icon="mdi-receipt"
              />
            </v-col> -->
  
            <!-- <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.emis"
                label="EMIS"
                outlined
                dense
                @change="updateField('emis')"
                prepend-inner-icon="mdi-school-outline"
              />
            </v-col> -->
          </v-row>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 2: General Description -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-text-box-outline</v-icon> File Overview
          </v-subheader>
          
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="form.description"
                label="File Title / Summary Description *"
                outlined
                dense
                @change="updateField('description')"
                prepend-inner-icon="mdi-text-short"
              />
            </v-col>
          </v-row>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 3: Private Practice Specifics (patient_files) -->
          <template v-if="isPrivatePractice">
            <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
              <v-icon left color="primary">mdi-medical-bag</v-icon> Medical & Billing Details
            </v-subheader>
  
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.icd10_code"
                  label="ICD-10 Diagnostic Code"
                  outlined
                  dense
                  @change="updateField('icd10_code')"
                  prepend-inner-icon="mdi-barcode"
                />
              </v-col>
  
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.medical_aid_name"
                  label="Medical Aid Scheme Name"
                  outlined
                  dense
                  @change="updateField('medical_aid_name')"
                  prepend-inner-icon="mdi-card-account-details"
                />
              </v-col>
  
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.medical_aid_number"
                  label="Medical Aid Number"
                  outlined
                  dense
                  @change="updateField('medical_aid_number')"
                  prepend-inner-icon="mdi-numeric"
                />
              </v-col>
            </v-row>
          </template>
  
          <!-- Section 3: Social Work Specifics (social_work_files) -->
          <template v-if="isSocialWork">
            <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
              <v-icon left color="primary">mdi-clipboard-account-outline</v-icon> Social Work & Intervention Details
            </v-subheader>
  
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.case_type"
                  label="Case Type"
                  outlined
                  dense
                  placeholder="e.g. Family, Foster Care, Trauma"
                  @change="updateField('case_type')"
                  prepend-inner-icon="mdi-shape"
                />
              </v-col>
  
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="form.referral_source"
                  label="Referral Source"
                  outlined
                  dense
                  placeholder="e.g. Court, School, Self"
                  @change="updateField('referral_source')"
                  prepend-inner-icon="mdi-account-arrow-right"
                />
              </v-col>
  
              <v-col cols="12" sm="4">
                <v-select
                  v-model="form.confidentiality_level"
                  :items="confidentialityOptions"
                  label="Confidentiality Level"
                  outlined
                  dense
                  @change="updateField('confidentiality_level')"
                  prepend-inner-icon="mdi-shield-lock-outline"
                />
              </v-col>
            </v-row>
          </template>
  
          <v-divider class="my-3"></v-divider>
  
          <!-- Section 4: Clinical Notes -->
          <v-subheader class="font-weight-bold pl-0 text-subtitle-1 primary--text">
            <v-icon left color="primary">mdi-note-text-outline</v-icon> Clinical & Case Notes
          </v-subheader>
  
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                v-model="form.notes"
                label="Notes / Progress Comments"
                outlined
                rows="4"
                dense
                @change="updateField('notes')"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
  
      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="confirmDeleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6 error--text">
            <v-icon left color="error">mdi-alert-circle</v-icon> Delete File Record?
          </v-card-title>
          <v-card-text>
            Are you sure you want to delete file reference
            <strong>{{ form.reference_number || form.description || 'this record' }}</strong>? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="confirmDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" @click="handleDelete">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import helpersJs from "@/plugins/helpers";
  
  export default {
    name: 'PracticeDetailCard',
    props: {
      selectedFile: {
        type: Object,
        default: null,
      },
    },
    computed: {
      getAccessToken() {
        return this.$store.getters.getAccessToken;
      },
      getAppMode() {
        return this.$store.getters.getAppMode || 'privatePractice';
      },
      isPrivatePractice() {
        return this.getAppMode === 'privatePractice';
      },
      isSocialWork() {
        return this.getAppMode === 'social';
      },
      // Dynamically routes queries to the correct database table
      activeTableName() {
        return this.isSocialWork ? 'social_work_files' : 'patient_files';
      },
    },
    data() {
      return {
        isFormValid: true,
        saving: false,
        deleting: false,
        confirmDeleteDialog: false,
        confidentialityOptions: [
          { text: 'Standard', value: 'standard' },
          { text: 'Sensitive', value: 'sensitive' },
          { text: 'Restricted', value: 'restricted' },
          { text: 'High Security', value: 'high_security' },
        ],
        form: this.getEmptyForm(),
      };
    },
    watch: {
      selectedFile: {
        immediate: true,
        deep: true,
        handler(newVal) {
          if (newVal) {
            this.form = {
              ...this.getEmptyForm(),
              ...newVal,
            };
          } else {
            this.form = this.getEmptyForm();
          }
        },
      },
    },
    methods: {
      getEmptyForm() {
        return {
          id: null,
          reference_number: '',
          client_reference: '',
          practitioner_reference: '',
          invoice_number: '',
          description: '',
          emis: '',
          notes: '',
          // patient_files specific
          icd10_code: '',
          medical_aid_name: '',
          medical_aid_number: '',
          // social_work_files specific
          case_type: '',
          referral_source: '',
          confidentiality_level: 'standard',
        };
      },
      async updateField(columnName) {
        if (!this.form.id) return;
  
        const payloadValue = this.form[columnName];
        await this.updateModelSingleRow(this.activeTableName, columnName, this.form.id, payloadValue);
      },
      async updateModelSingleRow(table, column, id, value) {
        this.$store.commit("setLoader", true);
        try {
          await helpersJs.updateModelSingleRow(table, column, id, value);
          this.$toast.success("Data updated successfully!", { duration: 4000 });
          this.$emit('save', this.form);
        } catch (error) {
          this.$toast.error("Failed to update record details.");
        } finally {
          this.$store.commit("setLoader", false);
        }
      },
      async handleDelete() {
        this.deleting = true;
        try {
          this.$emit('delete', { id: this.form.id, table: this.activeTableName });
          this.confirmDeleteDialog = false;
        } finally {
          this.deleting = false;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .v-subheader {
    height: 36px;
  }
  </style>